import { IElementWaypoint } from "@/hooks/use-placeholders";
import { DISABLED_VALUE_COLOR } from "@/utils/waypoints-color-gradient";
import { blue } from "@faro-lotv/flat-ui";
import { GUID } from "@faro-lotv/ielement-types";
import { assert } from "@faro-lotv/lotv";
import { useMemo } from "react";
import { Color } from "three";

/**
 * Record storing color overrides for specific elements.
 * The undefined value is allowed, to make sure that all record accesses are checked against undefined with TS,
 * since this mapping does not have to be completely defined for all elements.
 */
type ColorOverrides = Record<GUID, Color | undefined>;

/**
 * @returns Combines a list of base colors for waypoints with a record of color overrides.
 * @param elements The list of elements
 * @param colorsBase A list of base colors for each element
 * @param colorOverrides The color overrides to apply to the elements
 */
export function useColorOverrides(
  elements: IElementWaypoint[],
  colorsBase: Color[],
  colorOverrides: ColorOverrides,
): Color[] {
  return useMemo(() => {
    if (colorsBase.length === 0 && !Object.keys(colorOverrides).length) {
      return colorsBase;
    }

    assert(
      colorsBase.length === elements.length,
      "Expected the amount of colors to match the amount of elements",
    );

    return colorsBase.map(
      (baseColor, index) => colorOverrides[elements[index].id] ?? baseColor,
    );
  }, [colorOverrides, colorsBase, elements]);
}

/**
 * @returns A map of color overrides for elements to apply, while the move interaction is enabled. Empty when the interaction is disabled.
 * @param waypoints The list of waypoints being displayed
 * @param isMoveEnabled Whether the move interaction is enabled
 * @param highlightActiveWaypoint Whether the active waypoint should be highlighted in blue
 * @param activeWaypoint The currently active waypoint, if available
 * @param isPlaceholderDisabledForMove Function to compute on demand, whether a placeholder is allowed to be moved.
 * @param isPlaceholderDisabledForClick Function to compute on demand, whether a placeholder is allowed to be clicked.
 */
export function useMoveInteractionColorOverrides(
  waypoints: IElementWaypoint[],
  isMoveEnabled: boolean | undefined,
  highlightActiveWaypoint?: boolean,
  activeWaypoint?: IElementWaypoint,
  isPlaceholderDisabledForMove?: (el: IElementWaypoint) => false | string,
  isPlaceholderDisabledForClick?: (el: IElementWaypoint) => boolean,
): ColorOverrides {
  return useMemo(() => {
    if (
      (!isMoveEnabled || !isPlaceholderDisabledForMove) &&
      !isPlaceholderDisabledForClick
    ) {
      // Avoid iterating if no color overrides are needed.
      return {};
    }

    return waypoints.reduce<ColorOverrides>((overrides, el) => {
      if (
        isMoveEnabled &&
        highlightActiveWaypoint &&
        el.id === activeWaypoint?.id
      ) {
        // Color matching the user location marker
        overrides[el.id] = new Color(blue[800]);
      }

      if (
        (!isMoveEnabled || isPlaceholderDisabledForMove?.(el)) &&
        isPlaceholderDisabledForClick?.(el)
      ) {
        overrides[el.id] = new Color(DISABLED_VALUE_COLOR);
      }
      return overrides;
    }, {});
  }, [
    isMoveEnabled,
    isPlaceholderDisabledForMove,
    isPlaceholderDisabledForClick,
    waypoints,
    highlightActiveWaypoint,
    activeWaypoint?.id,
  ]);
}
